import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, Building2, Handshake, Wrench, Truck, Loader2 } from "lucide-react";
import Layout from "@/components/Layout";
import SectionHeader from "@/components/SectionHeader";
import SuccessCard from "@/components/SuccessCard";
import { useI18n } from "@/i18n";
import { api } from "@/lib/api";
import { brandAssets } from "@/data/brand";

const initial = {
  business_name: "", contact_person: "", phone: "", email: "",
  website: "", city: "", business_type: "", monthly_volume: "",
  equipment: "", message: "", hp: "",
};

export default function Partners() {
  const { t, lang } = useI18n();
  const [f, setF] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const target = t("partners.target");
  const benefits = t("partners.benefits.items");
  const fields = t("partners.form.fields");
  const targetIcons = [Wrench, Building2, Handshake, Truck];

  const set = (k, v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (loading || submitted) return;
    if (f.hp) return;
    setLoading(true);
    try {
      await api.post("/partner", { ...f, language: lang });
      setF(initial);
      setSubmitted(true);
    } catch {
      toast.error(t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => setSubmitted(false);

  return (
    <Layout seoTitle={t("partners.title")} seoDescription={t("partners.subtitle")}>
      <section className="relative section-y overflow-hidden border-b border-white/[0.06]">
        <div className="absolute inset-0 z-0">
          <img src={brandAssets.eventBooth} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-[0.5]" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black" />
        </div>
        <div className="container-mt relative z-10">
          <SectionHeader as="h1" title={t("partners.title")} subtitle={t("partners.subtitle")} />
        </div>
      </section>

      <section className="section-y">
        <div className="container-mt grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {target.map((it, i) => {
                const Icon = targetIcons[i % targetIcons.length];
                return (
                  <div key={i} className="card-mt card-mt-pad card-interactive">
                    <div className="icon-tile mb-5"><Icon size={18} /></div>
                    <span className="num-chip mb-4">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="h-display text-xl text-white mt-4 mb-2">{it.t}</h3>
                    <p className="text-zinc-400 text-sm leading-relaxed">{it.d}</p>
                  </div>
                );
              })}
            </div>
          </div>
          <div>
            <h2 className="h-display text-3xl md:text-4xl text-white mb-8">{t("partners.benefits.title")}</h2>
            <ul className="space-y-3">
              {benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-3 card-mt card-mt-pad !p-4">
                  <CheckCircle2 size={16} className="text-brand-lime mt-1 shrink-0" />
                  <span className="text-zinc-200 text-sm">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="section-y border-t border-white/[0.06]">
        <div className="container-mt">
          {submitted ? (
            <div data-testid="partner-success" className="max-w-2xl">
              <SuccessCard
                testid="partner-success-card"
                title={t("successCards.partnerTitle")}
                body={t("successCards.partnerBody")}
              />
              <button type="button" onClick={reset} data-testid="partner-submit-another" className="btn-outline mt-6">
                {t("common.submitAnother")}
              </button>
            </div>
          ) : (
            <>
              <SectionHeader title={t("partners.form.title")} />
              <form onSubmit={submit} data-testid="partner-form" noValidate className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 max-w-4xl">
                <input
                  type="text"
                  name="website_url"
                  value={f.hp}
                  onChange={(e) => set("hp", e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
                />
                {[
                  ["business_name", true],
                  ["contact_person", true],
                  ["phone", true],
                  ["email", true],
                  ["website", false],
                  ["city", true],
                  ["business_type", true],
                  ["monthly_volume", false],
                ].map(([k, req]) => (
                  <div key={k}>
                    <label htmlFor={`partner-${k}`} className="label-mt">{fields[k]}{req && " *"}</label>
                    <input
                      id={`partner-${k}`}
                      required={req}
                      value={f[k]}
                      onChange={(e) => set(k, e.target.value)}
                      className="input-mt"
                      data-testid={`partner-${k}`}
                      type={k === "email" ? "email" : k === "phone" ? "tel" : "text"}
                      autoComplete={k === "email" ? "email" : k === "phone" ? "tel" : "off"}
                    />
                  </div>
                ))}
                <div className="md:col-span-2">
                  <label htmlFor="partner-equipment" className="label-mt">{fields.equipment}</label>
                  <input id="partner-equipment" value={f.equipment} onChange={(e) => set("equipment", e.target.value)} className="input-mt" data-testid="partner-equipment" />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="partner-message" className="label-mt">{fields.message} *</label>
                  <textarea id="partner-message" required rows={5} value={f.message} onChange={(e) => set("message", e.target.value)} className="input-mt resize-none" data-testid="partner-message" />
                </div>
                <div className="md:col-span-2 pt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="partner-submit"
                    aria-busy={loading}
                    className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {loading ? (<><Loader2 size={14} className="animate-spin" /> {t("common.submitting")}</>) : (<>{fields.submit} <ArrowRight size={14} /></>)}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
